import { useState } from "react";
import { Truck, AlertTriangle, Clock } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { PageHeader, SectionCard, KpiCard, Loader, EmptyState, TABLE_HEAD, TABLE_ROW } from "../components/common";
import { DebtorDialog } from "../components/DebtorDialog";
import { useApi } from "../hooks/useApi";
import { useDepot } from "../context/DepotContext";
import { money, TZ } from "../lib/api";

const STATUS = {
  overdue: ["Overdue", "text-rose-400 bg-rose-500/10 border-rose-500/30"],
  due_soon: ["Due soon", "text-amber-400 bg-amber-500/10 border-amber-500/30"],
  ok: ["On time", "text-slate-300 bg-slate-800 border-slate-700"],
  settled: ["Settled", "text-emerald-400 bg-emerald-500/10 border-emerald-500/30"],
};

function StatusBadge({ status, testid }) {
  const [label, cls] = STATUS[status] || STATUS.ok;
  return <span className={`inline-flex rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${cls}`} data-testid={testid}>{label}</span>;
}

export default function Payables() {
  const { depotParam, scopeLabel } = useDepot();
  const [showSettled, setShowSettled] = useState(false);
  const [active, setActive] = useState(null);
  const { data, reload } = useApi("/creditors", { depot_id: depotParam, include_settled: showSettled, tz: TZ });

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={scopeLabel} title="Supplier Credit" subtitle="Stock bought on credit and what you still owe each supplier. Payments clear the oldest bills first."
        actions={
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <Switch checked={showSettled} onCheckedChange={setShowSettled} data-testid="payables-show-settled-switch" /> Show settled
          </label>
        } />
      {!data ? <Loader /> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <KpiCard label="Owed to suppliers" value={money(data.outstanding)} sub={`${data.count} suppliers`} icon={Truck} tone="amber" testid="payables-outstanding-total" />
            <KpiCard label="Overdue" value={money(data.overdue_total)} sub={`${data.overdue_count} suppliers`} icon={AlertTriangle} tone="rose" testid="payables-overdue-total" delay={60} />
            <KpiCard label="Due in 3 days" value={money(data.due_soon_total)} sub={`${data.due_soon_count} suppliers`} icon={Clock} tone="blue" testid="payables-due-soon-total" delay={120} />
          </div>
          <SectionCard title={`${data.rows.length} suppliers`}>
            {!data.rows.length ? <EmptyState text="Nothing owed. Stock In with payment 'Credit' appears here." testid="payables-empty" /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="payables-table">
                  <thead className={TABLE_HEAD}>
                    <tr>
                      <th className="px-4 py-2.5 text-left">Supplier</th><th className="px-4 py-2.5 text-left">Depot</th>
                      <th className="px-4 py-2.5 text-right">On credit</th><th className="px-4 py-2.5 text-right">Paid</th>
                      <th className="px-4 py-2.5 text-right">Balance</th><th className="px-4 py-2.5 text-left">Next due</th>
                      <th className="px-4 py-2.5 text-left">Status</th><th className="px-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r, i) => (
                      <tr key={`${r.supplier_key}-${r.depot_id}`} className={TABLE_ROW} data-testid={`payable-row-${i}`}>
                        <td className="px-4 py-2.5"><div className="font-medium">{r.supplier}</div><div className="text-xs text-slate-500">{r.sales_count} credit purchases</div></td>
                        <td className="px-4 py-2.5 text-slate-300">{r.depot_name}</td>
                        <td className="px-4 py-2.5 text-right font-mono">{money(r.total_credit)}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-emerald-400">{money(r.total_paid)}</td>
                        <td className="px-4 py-2.5 text-right font-mono font-semibold text-amber-400" data-testid={`payable-balance-${i}`}>{money(r.balance)}</td>
                        <td className="px-4 py-2.5 font-mono text-xs text-slate-300">{r.next_due || "—"}</td>
                        <td className="px-4 py-2.5"><StatusBadge status={r.status} testid={`payable-status-${i}`} /></td>
                        <td className="px-2 py-2.5 text-right">
                          <Button size="sm" variant="outline" className="border-slate-700" onClick={() => setActive(r)} data-testid={`payable-open-button-${i}`}>
                            {r.balance > 0 ? "Pay supplier" : "View"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </>
      )}
      <DebtorDialog side="supplier" row={active} onOpenChange={(o) => { if (!o) setActive(null); }} onChanged={reload} />
    </div>
  );
}
